// EnterPasswordComponent.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";

import {
  useI18N,
  useLogin,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import Loader from "@/components/common/Loader";

import {
  emailAtom,
  emailCheckWelcomeNameAtom,
  onboardingCompletedAtom,
  onboardingCurrentScreenAtom,
  onboardingScreenAtom,
  passwordAtom,
} from "@/store/atoms";
import { useDarkMode } from "@/hooks/useDarkMode";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import WideButton from "@/components/common/WideButton";
import { ArrowLeft } from "lucide-react";
import { StandardButton } from "@/components/common/StandardButton";
import { trackEvent, createPageAnalytics } from "common/analytics";
import { applyUserIdentity } from "@/analytics/identity";

// Map a rejected login onto the account_login_failed `error_type` controlled vocabulary. useLogin's
// performLogin THROWS new Error(backendMessage) on a rejected login and a TypeError ("Failed to
// fetch") on a transport failure, so the failure arrives as the mutation `error`. Default to
// wrong_password (the dominant rejected-password case); mirrors apps/business' classifyLoginError.
function classifyLoginError(
  err: unknown,
): "wrong_password" | "auth_method_mismatch" | "account_not_found" | "account_locked" | "network_error" {
  const message = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  if (!message || message.includes("fetch") || message.includes("network")) {
    return "network_error";
  }
  if (
    message.includes("not found") ||
    message.includes("no account") ||
    message.includes("does not exist") ||
    message.includes("doesn't exist")
  ) {
    return "account_not_found";
  }
  if (message.includes("locked") || message.includes("too many")) {
    return "account_locked";
  }
  if (
    message.includes("social") ||
    message.includes("google") ||
    message.includes("facebook") ||
    message.includes("apple") ||
    message.includes("microsoft") ||
    message.includes("method")
  ) {
    return "auth_method_mismatch";
  }
  return "wrong_password";
}

interface EnterPasswordProps {
  onClose: () => void;
  title: string;
  onBack: () => void;
}

const EnterPasswordComponent = ({
  onClose,
  onBack,
  title,
}: EnterPasswordProps) => {
  const isZoomedIn = useZoomDetection();
  const i18n = useI18N();
  const isDark = useDarkMode();
  const [email] = useAtom(emailAtom);
  const [password, setPassword] = useAtom(passwordAtom);
  const [emailCheckWelcomeName] = useAtom(emailCheckWelcomeNameAtom);
  const { setScreenIndex } = useAuthModals();
  const { modalStyle, modalClassName } = useModalHeight();
  const { performLogin, error, result, isLoading } = useLogin();

  const authPasswordAnalytics = createPageAnalytics("auth_password");

  const [, setIsOnboardingModalOpen] = useAtom(onboardingScreenAtom);
  const [, setOnboardingCurrentScreen] = useAtom(onboardingCurrentScreenAtom);
  const [, setIsOnboardingCompleted] = useAtom(onboardingCompletedAtom);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handlePasswordContinue = async () => {
    // console.log("password entered call performLogin", password);
    performLogin({ email: email, password });
  };

  const handleOpenOnboardingSliderModal = () => {
    console.log("in EPC handleOpenOnboardingSliderModal");
    setOnboardingCurrentScreen(0);
    setIsOnboardingModalOpen(true);
    setIsOnboardingCompleted(false);
  };

  useEffect(() => {
    authPasswordAnalytics.track("page_view_page", {}); // auth_password_viewed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (result) {
      // performLogin throws on a rejected login (handled in the `error` effect below), so a present
      // `result` is always a success.
      const userData = result.data;

      // Identify BEFORE the event so account_logged_in carries the real user id rather than the
      // anonymous $device id (or a stale numeric id left over from a prior unverified signup).
      // identify() updates the distinct id synchronously, and applyUserIdentity resets first when
      // switching accounts so the two identities never merge.
      applyUserIdentity(userData);

      // Email/password login path → auth_provider "email". brand_id/brand_name are optional (no
      // brand context in the consumer app).
      trackEvent("account_logged_in", { auth_provider: "email" });

      console.log("Login done");

      if (userData.firstLogin == 1) {
        //call open onboarding slider
        handleOpenOnboardingSliderModal();
      }

      setScreenIndex(AuthModalScreen.EnterEmail);
      setPassword("");
      onClose();
    }
  }, [result, setScreenIndex, onClose]);

  useEffect(() => {
    if (error) {
      console.log("500 Error", error);
      // account_login_failed — performLogin (useLogin) THROWS on a rejected login, so the failure
      // surfaces here as `error`, not as result.status === "error". email/password path →
      // auth_provider "email"; error_type classified from the thrown message.
      trackEvent("account_login_failed", {
        auth_provider: "email",
        error_type: classifyLoginError(error),
      });
    }
  }, [error]);

  const handleForgotClick = async () => {
    authPasswordAnalytics.track("forgot_password_link_button", {});

    //Show forgot password
    setScreenIndex(AuthModalScreen.ForgotPassword);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <section
      style={modalStyle}
      className="relative h-full bg-bg-brand-secondary-default rounded-xl flex flex-col overflow-y-auto"
      role="none"
      aria-labelledby="modal-title"
    >
      <header
        className={`py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
      >
        <StandardButton
          onClick={() => {
            onBack();
            setPassword("");
          }}
          variant="back"
          icon={<ArrowLeft color={isDark ? "#A39FE8" : "#352FC3"} />}
          aria-label="Go back"
          tabIndex={0}
        />
        <h1
          className="title-s-semibold text-center text-fg-neutral-strong"
          ref={firstFocusRef}
          tabIndex={0}
          id="modal-title"
        >
          {title}
        </h1>
      </header>
      <div className="flex-1 flex-grow px-8 md:px-16 pt-4 flex flex-col">
        <div className="flex justify-center mb-6">
          <img src="/images/Group.svg" alt="" aria-hidden="true" />
        </div>
        <div className="mb-6">
          <h2 className="title-m-bold text-fg-neutral-strong text-center">
            {`Welcome back, ${emailCheckWelcomeName}!`}
          </h2>
        </div>
        <div className="mb-4">
          Enter your password below to start finding accessible places
        </div>
        <hr className="w-full border-border-token-brand-minimal mb-6" />
        <div className="mb-6">
          <FloatingLabelInput
            label="Password"
            placeholder="Password"
            required
            onChange={handlePasswordChange}
            value={password}
            type="Password"
            errorMessage={error?.message}
            onToggleVisibility={(visible) =>
              authPasswordAnalytics.track("password_visibility_toggle_toggle", {
                visibility_state: visible ? "shown" : "hidden",
              })
            }
          />
        </div>
        <div className="flex-1"></div>
        <div className="mb-8 items-center justify-center flex flex-col">
          <WideButton
            type="filled"
            disabled={password.length <= 0}
            onClick={handlePasswordContinue}
          >
            {i18n.get("continue")}
          </WideButton>
          <StandardButton
            onClick={() => {
              handleForgotClick();
              setPassword("");
            }}
            variant="link"
            className="label-s-medium underline mt-6"
          >
            {i18n.get("forgotYourPassword")}
          </StandardButton>
        </div>
      </div>

      {isLoading && <Loader />}
    </section>
  );
};

export default EnterPasswordComponent;
